/**
 * Canada Post–style domestic helpers (Oversized Lettermail + flat-rate box cap).
 * weight_thresholds JSON for CA can be either:
 * - Legacy: [ { weight_kg, price_cents }, ... ]
 * - Extended: { lettermail_bands, flat_rate_boxes_cents, lettermail_max_kg, ... }
 */

export function normalizeWeightThresholds(raw) {
  if (raw == null) return { bands: [], canadaExtended: null };
  let parsed = raw;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { bands: [], canadaExtended: null };
    }
  }
  if (Array.isArray(parsed)) {
    return { bands: parsed, canadaExtended: null };
  }
  const bands = parsed.lettermail_bands || parsed.bands || [];
  const hasCanada =
    (parsed.flat_rate_boxes && parsed.flat_rate_boxes.length) ||
    parsed.flat_rate_boxes_cents?.length ||
    parsed.lettermail_max_kg != null ||
    parsed.xpresspost_1kg_cents != null ||
    (parsed.parcel_bands && parsed.parcel_bands.length);
  return {
    bands,
    canadaExtended: hasCanada || bands.length ? parsed : null
  };
}

export function lettermailPriceCents(weightKg, bands, maxKg = 0.5) {
  if (weightKg == null || weightKg <= 0 || !bands?.length || weightKg > maxKg) return null;
  const sorted = [...bands].sort((a, b) => a.weight_kg - b.weight_kg);
  const t = sorted.find((x) => x.weight_kg >= weightKg);
  return t ? t.price_cents : sorted[sorted.length - 1].price_cents;
}

/**
 * Flat boxes: either [{ price_cents, max_length_cm }, ...] or legacy cents only (any length).
 */
export function normalizeFlatBoxes(extended) {
  if (!extended) return [];
  if (Array.isArray(extended.flat_rate_boxes) && extended.flat_rate_boxes.length > 0) {
    return extended.flat_rate_boxes.map((b) => ({
      price_cents: Number(b.price_cents),
      max_length_cm: Number(b.max_length_cm)
    }));
  }
  const cents = extended.flat_rate_boxes_cents;
  if (Array.isArray(cents) && cents.length > 0) {
    return cents.map((c) => ({ price_cents: Number(c), max_length_cm: Infinity }));
  }
  return [];
}

/** Cheapest prepaid box that fits longest item edge (cm). Missing max_length_cm = no cap (legacy). */
export function cheapestEligibleFlatCents(boxes, requiredLongestCm) {
  if (!boxes?.length) return null;
  const req = Math.max(0, Number(requiredLongestCm) || 0);
  const eligible = boxes.filter((b) => {
    const cap = b.max_length_cm != null ? Number(b.max_length_cm) : Infinity;
    return cap >= req;
  });
  if (!eligible.length) return null;
  return Math.min(...eligible.map((b) => b.price_cents));
}

/** Look up a price from a sorted weight-band array (finds first band where weight_kg >= target). */
export function bandPriceCents(weightKg, bands) {
  if (!bands?.length || weightKg <= 0) return null;
  const sorted = [...bands].sort((a, b) => a.weight_kg - b.weight_kg);
  const hit = sorted.find((b) => b.weight_kg >= weightKg);
  return hit ? hit.price_cents : sorted[sorted.length - 1].price_cents;
}

/**
 * Standard domestic: cheapest of eligible Oversized Lettermail (≤500g) vs smallest *fitting*
 * flat-rate box (≤5kg; filtered by max interior length) vs parcel band vs regular parcel
 * over max flat weight.
 *
 * @param {number} requiredLongestCm — max longest side (cm) across shipment; e.g. 35.56 for 14" legal.
 */
/**
 * Apply fuel surcharge to a base cost in cents.
 * @param {number} baseCents - Base price before fuel surcharge
 * @param {number} fuelPct - Fuel surcharge percentage (e.g. 33.5 = 33.5%)
 * @returns {{ baseCents: number, fuelCents: number, totalCents: number }}
 */
function applyFuelSurcharge(baseCents, fuelPct) {
  if (!fuelPct || fuelPct <= 0) return { baseCents, fuelCents: 0, totalCents: baseCents };
  const fuelCents = Math.round(baseCents * fuelPct / 100);
  return { baseCents, fuelCents, totalCents: baseCents + fuelCents };
}

/**
 * Canada Post volumetric weight: max(actual, L×W×H / divisor).
 * CP rounds billable weight up to nearest 0.5 kg when > 0.75 kg.
 * @param {number} actualKg
 * @param {number} lengthCm
 * @param {number} widthCm
 * @param {number} heightCm
 * @param {number} divisor - 6000 for Regular Parcel (default)
 * @returns {{ billableKg: number, volumetricKg: number, wasVolumetric: boolean }}
 */
export function volumetricBillableWeight(actualKg, lengthCm, widthCm, heightCm, divisor = 6000) {
  const volKg = (lengthCm && widthCm && heightCm) ? (lengthCm * widthCm * heightCm) / divisor : 0;
  let billable = Math.max(actualKg, volKg);
  // CP rounds up to nearest 0.5 kg when billable > 0.75 kg
  if (billable > 0.75) {
    billable = Math.ceil(billable * 2) / 2;
  }
  return { billableKg: billable, volumetricKg: volKg, wasVolumetric: volKg > actualKg };
}

export function canadaCheapestStandard(weightKg, extended, standardRateDollars, requiredLongestCm = 0) {
  const lettermailBands = extended?.lettermail_bands || extended?.bands || [];
  const parcelBands = extended?.parcel_bands || [];
  const maxLetter = extended?.lettermail_max_kg ?? 0.5;
  const boxes = normalizeFlatBoxes(extended);
  const maxFlatKg = extended?.flat_rate_max_kg ?? 5;
  const stdCents = Math.round(Number(standardRateDollars) * 100) || 1600;
  const extraPerKg = extended?.regular_parcel_extra_per_kg_cents ?? 100;
  const fuelPct = Number(extended?.fuel_surcharge_pct) || 0;

  // Each candidate: { cost, label, baseCents, fuelCents }
  const candidates = [];

  // Option 1: Oversized Lettermail (light items ≤ 500g) — no fuel surcharge on lettermail
  const letter = lettermailPriceCents(weightKg, lettermailBands, maxLetter);
  if (letter != null) {
    const matchedBand = [...lettermailBands].sort((a, b) => a.weight_kg - b.weight_kg).find(b => b.weight_kg >= weightKg);
    candidates.push({ cost: letter, baseCents: letter, fuelCents: 0, label: `Lettermail ${matchedBand ? matchedBand.weight_kg : weightKg} kg` });
  }

  // Option 2: Flat-rate prepaid box (≤ 5kg, must fit longest side) — flat-rate boxes are all-in, no fuel surcharge
  if (weightKg <= maxFlatKg && boxes.length > 0) {
    const flatCents = cheapestEligibleFlatCents(boxes, requiredLongestCm);
    if (flatCents != null) {
      const req = Math.max(0, Number(requiredLongestCm) || 0);
      const eligible = boxes.filter(b => (b.max_length_cm ?? Infinity) >= req);
      const cheapestBox = eligible.reduce((best, b) => (!best || b.price_cents < best.price_cents) ? b : best, null);
      const sizeNames = { 22.5: 'Extra Small', 35: 'Small', 39: 'Medium', 40.3: 'Large' };
      const sizeName = cheapestBox ? (sizeNames[cheapestBox.max_length_cm] || `${cheapestBox.max_length_cm}cm`) : '';
      candidates.push({ cost: flatCents, baseCents: flatCents, fuelCents: 0, label: `Flat-Rate Box (${sizeName})` });
    }
  }

  // Option 3: Parcel bands (0.5kg–10kg+, weight-based tiers) — fuel surcharge applies
  if (parcelBands.length > 0 && weightKg > maxLetter) {
    const parcelBase = bandPriceCents(weightKg, parcelBands);
    if (parcelBase != null) {
      const { baseCents, fuelCents, totalCents } = applyFuelSurcharge(parcelBase, fuelPct);
      const matchedBand = [...parcelBands].sort((a, b) => a.weight_kg - b.weight_kg).find(b => b.weight_kg >= weightKg);
      candidates.push({ cost: totalCents, baseCents, fuelCents, label: `Parcel ${matchedBand ? matchedBand.weight_kg : weightKg} kg band` });
    }
  }

  // Option 4: Regular Parcel fallback (standard rate + extra per kg above max flat weight) — fuel surcharge applies
  if (weightKg > maxFlatKg) {
    const fallbackBase = stdCents + Math.round((weightKg - maxFlatKg) * extraPerKg);
    const { baseCents, fuelCents, totalCents } = applyFuelSurcharge(fallbackBase, fuelPct);
    candidates.push({ cost: totalCents, baseCents, fuelCents, label: `Regular Parcel (${weightKg} kg)` });
  }

  if (!candidates.length) {
    if (parcelBands.length > 0) {
      const fallbackBase = bandPriceCents(weightKg, parcelBands);
      const { baseCents, fuelCents, totalCents } = applyFuelSurcharge(fallbackBase ?? stdCents, fuelPct);
      return { cost: totalCents, baseCents, fuelCents, label: `Parcel fallback (${weightKg} kg)` };
    }
    return { cost: stdCents, baseCents: stdCents, fuelCents: 0, label: 'Standard rate' };
  }

  return candidates.reduce((best, c) => (c.cost < best.cost ? c : best));
}

export function parseWeightThresholdsForEdit(raw) {
  let bands = [];
  let caPostExtras = null;
  if (!raw) return { bands, caPostExtras };
  try {
    const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(p)) return { bands: p, caPostExtras: null };
    bands = p.lettermail_bands || p.bands || [];
    const copy = { ...p };
    delete copy.lettermail_bands;
    delete copy.bands;
    if (Object.keys(copy).length) caPostExtras = copy;
    return { bands, caPostExtras };
  } catch {
    return { bands: [], caPostExtras: null };
  }
}
